import { AnyError, ErrorAlert } from '@if/mui'
import { Box, CircularProgress } from '@mui/material'
import { CalendarSimulation, SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { memo } from 'react'
import LoanCalendarActionPreviewTable from './LoanCalendarActionPreviewTable'

interface LoanCalendarActionPreviewBodyProps {
  /** The calendar as it stands — the "before" column. */
  simulationBefore: CalendarSimulation
  /** The settled recompute, or `null` while none matches the fields on screen. */
  preview: SignedCalendarSimulation | null
  /** True while a preview is owed: the fields are usable, the recompute has not landed yet. */
  recomputing: boolean
  actionDate?: Date | null
  error?: AnyError
}

/**
 * The before/after band the three recomputing calendar dialogs share. With no settled
 * preview the "after" column repeats the "before" one — the honest reading, since nothing
 * has been computed for the values on screen and there is nothing to confirm either.
 */
const LoanCalendarActionPreviewBody = ({
  simulationBefore,
  preview,
  recomputing,
  actionDate,
  error,
}: LoanCalendarActionPreviewBodyProps) => {
  // A failed recompute leaves no preview to show, but nothing is in flight either — the alert
  // is the answer at that point, not a spinner that would never stop.
  const inFlight = recomputing && !error

  return (
    <Box>
      {error && <ErrorAlert error={error} />}
      <Box sx={{ position: 'relative' }}>
        <Box sx={{ opacity: inFlight ? 0.4 : 1, transition: 'opacity 150ms' }}>
          <LoanCalendarActionPreviewTable
            simulationBefore={simulationBefore}
            simulationAfter={(preview ?? { simulation: simulationBefore }).simulation}
            actionDate={actionDate ?? undefined}
          />
        </Box>
        {inFlight && (
          <Box
            sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            aria-hidden
          >
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default memo(LoanCalendarActionPreviewBody)
